import { S, ui } from "../app/store.js";
import { esc } from "../domain/format.js";
import { request } from "../services/api.js";
import { captchaSlot, captchaHeaders, resetCaptcha } from "../components/turnstile.js";

let resetToken = null;
let resendAt = 0;
let resendTimer;
let installed = false;
const post = (url, body, headers = {}) =>
  request(url, { method: "POST", body: JSON.stringify(body), headers });
const repaint = () =>
  globalThis.dispatchEvent(new CustomEvent("milltech:render"));
const minimum = () => {
  const value = Number(
    ui.passwordMinimum || ui.passwordMin || S?.settings?.pwMin || 12,
  );
  return Number.isFinite(value) ? Math.max(12, Math.floor(value)) : 12;
};
const disabled = () => (ui.authPasswordBusy ? " disabled" : "");
const errorMarkup = () =>
  `<p class="auth-error" role="alert">${esc(ui.authPasswordError || "")}</p>`;
const passwordField = (name, label, current = false) =>
  `<label>${label}<input name="${name}" type="password" minlength="${current ? 12 : minimum()}" maxlength="128" autocomplete="${current ? "current-password" : "new-password"}" data-case="preserve" required></label>`;

export function passwordScreen() {
  if (!ui.authPasswordScreen) return null;
  if (ui.authPasswordScreen === "forgot") {
    return {
      title: "Forgot Your Password?",
      description: "Enter your work email to request a password reset link.",
      content: `<form id="password-request-reset"><label>Work Email<input name="email" type="email" autocomplete="username" data-case="preserve" value="${esc(ui.authDraft?.email || "")}" required></label>${captchaSlot()}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authPasswordBusy ? "Please Wait…" : "Send Reset Link"}</button><button type="button" data-password-action="sign-in"${disabled()}>Back To Sign-In</button></form>`,
    };
  }
  if (ui.authPasswordScreen === "forgot-sent") {
    const remaining = Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));
    return {
      title: "Check Your Email",
      description:
        "If an eligible account exists, a reset link has been sent. Check your inbox and spam folder.",
      content: `<div class="password-actions">${captchaSlot()}${errorMarkup()}<button type="button" data-password-action="resend-reset"${remaining || ui.authPasswordBusy ? " disabled" : ""}>${remaining ? `Send Again In ${remaining}s` : "Send Again"}</button><button type="button" data-password-action="sign-in"${disabled()}>Back To Sign-In</button></div>`,
    };
  }
  if (ui.authPasswordScreen === "reset-complete") {
    return {
      title: "Password Updated",
      description:
        "Your password has been reset. Sign in with your new password, then complete your usual account verification.",
      content:
        '<button class="pri" type="button" data-password-action="sign-in">Go To Sign-In</button>',
    };
  }
  if (ui.authPasswordScreen === "reset") {
    if (!resetToken) {
      return {
        title: "Request A New Reset Link",
        description: "This password reset link is invalid or has expired.",
        content: `<div class="password-actions">${errorMarkup()}<button class="pri" type="button" data-password-action="forgot">Request A New Link</button><button type="button" data-password-action="sign-in">Back To Sign-In</button></div>`,
      };
    }
    return {
      title: "Choose A New Password",
      description: `Use at least ${minimum()} characters. You will sign in again after resetting your password.`,
      content: `<form id="password-reset">${passwordField("newPassword", "New Password")}${passwordField("passwordConfirm", "Confirm New Password")}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authPasswordBusy ? "Please Wait…" : "Reset Password"}</button><button type="button" data-password-action="forgot"${disabled()}>Request A New Link</button></form>`,
    };
  }
  if (ui.authPasswordScreen === "change-complete") {
    return {
      title: "Password Updated",
      description:
        "Your password was updated. Sign out to finish and sign in again with your new password.",
      content: `<div class="password-actions">${errorMarkup()}<button class="pri" type="button" data-password-action="finish-change"${disabled()}>Sign Out And Continue</button></div>`,
    };
  }
  return {
    title: "Change Your Password",
    description: `Use at least ${minimum()} characters. Your other sessions will end, and you will sign in again.`,
    content: `<form id="password-change">${passwordField("currentPassword", "Current Password", true)}${passwordField("newPassword", "New Password")}${passwordField("passwordConfirm", "Confirm New Password")}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authPasswordBusy ? "Please Wait…" : "Change Password"}</button><button type="button" data-password-action="cancel-change"${disabled()}>Cancel</button></form>`,
  };
}

function showError(message) {
  ui.authPasswordError = message;
  const node = document.querySelector(".auth-error");
  if (node) node.textContent = message;
}
function setBusy(value) {
  ui.authPasswordBusy = value;
  document
    .querySelector(".auth-card")
    ?.setAttribute("aria-busy", String(value));
  for (const button of document.querySelectorAll(".auth-card button")) {
    if (value) {
      button.dataset.passwordDisabled = String(button.disabled);
      button.disabled = true;
    } else if (button.dataset.passwordDisabled !== undefined) {
      button.disabled = button.dataset.passwordDisabled === "true";
      delete button.dataset.passwordDisabled;
    }
  }
}
async function perform(action) {
  if (ui.authPasswordBusy) return;
  showError("");
  setBusy(true);
  try {
    await action();
  } catch (error) {
    showError(error.message || "This request could not be completed.");
  } finally {
    setBusy(false);
  }
}
function showScreen(screen) {
  clearInterval(resendTimer);
  ui.authPasswordScreen = screen;
  ui.authPasswordError = "";
  ui.authPasswordBusy = false;
  repaint();
}
export function openPasswordChange() {
  if (!ui.user || ui.authPendingMfa || ui.mfaEnrollmentRequired) return false;
  const user = S?.users?.find((entry) => entry.id === ui.user);
  if (user) ui.securityUser = { name: user.name, email: user.email };
  resetToken = null;
  ui.user = null;
  showScreen("change");
  document.querySelector('[name="currentPassword"]')?.focus();
  return true;
}
export function capturePasswordReset(value = globalThis.location.href) {
  const url = new URL(value, globalThis.location.origin);
  if (
    url.pathname !== "/reset-password" &&
    !url.searchParams.has("reset-password")
  )
    return false;
  const supplied = url.searchParams.get("token");
  resetToken = supplied && supplied.length <= 1024 ? supplied : null;
  if (url.searchParams.has("error")) resetToken = null;
  url.searchParams.delete("token");
  url.searchParams.delete("error");
  url.searchParams.delete("reset-password");
  history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  ui.user = null;
  ui.authPasswordScreen = "reset";
  ui.authPasswordBusy = false;
  ui.authPasswordError = "";
  return true;
}
function backToSignIn() {
  resetToken = null;
  clearInterval(resendTimer);
  ui.authPasswordScreen = null;
  ui.authPasswordError = "";
  ui.authPasswordBusy = false;
  if (
    location.pathname === "/reset-password" ||
    location.search?.includes("reset-password")
  )
    history.replaceState(null, "", "/");
  repaint();
}
function countdown() {
  clearInterval(resendTimer);
  const update = () => {
    const button = document.querySelector(
      '[data-password-action="resend-reset"]',
    );
    if (!button) {
      clearInterval(resendTimer);
      return;
    }
    const remaining = Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));
    button.textContent = remaining
      ? `Send Again In ${remaining}s`
      : "Send Again";
    button.disabled = !!remaining || !!ui.authPasswordBusy;
    if (!remaining) clearInterval(resendTimer);
  };
  update();
  resendTimer = setInterval(update, 1000);
  resendTimer.unref?.();
}
async function sendReset(email, container) {
  try {
    await post("/api/auth/request-password-reset", {
      email,
      redirectTo: `${location.origin}/reset-password`,
    }, captchaHeaders(container));
  } finally {
    resetCaptcha(container);
  }
  ui.authDraft = { ...ui.authDraft, email };
  resendAt = Date.now() + 60_000;
  showScreen("forgot-sent");
  countdown();
}
function matchingPasswords(data) {
  if (data.newPassword !== data.passwordConfirm) {
    showError("Your passwords do not match.");
    return false;
  }
  if (data.newPassword.length < minimum()) {
    showError(`Use a password of at least ${minimum()} characters.`);
    return false;
  }
  return true;
}
async function finishPasswordChange() {
  await post("/api/auth/sign-out", {});
  location.reload();
}
async function submitPassword(form) {
  const data = Object.fromEntries(new FormData(form));
  if (!matchingPasswords(data)) return;
  await perform(async () => {
    if (form.id === "password-change") {
      await post("/api/auth/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        revokeOtherSessions: true,
      });
      form.reset();
      showScreen("change-complete");
      setBusy(true);
      // The native change operation rotates the session. Finish by signing out
      // so the next login repeats the account's normal MFA verification.
      await finishPasswordChange();
      return;
    }
    if (!resetToken) {
      showScreen("reset");
      return;
    }
    try {
      await post("/api/auth/reset-password", {
        token: resetToken,
        newPassword: data.newPassword,
      });
    } catch (error) {
      if (/invalid|expired|token/i.test(error.message)) {
        resetToken = null;
        form.reset();
        showScreen("reset");
      }
      throw error;
    }
    resetToken = null;
    form.reset();
    showScreen("reset-complete");
  });
}
export function installPasswordEvents(root = document) {
  if (installed) return;
  installed = true;
  root.addEventListener("submit", (event) => {
    const form = event.target;
    if (
      !["password-request-reset", "password-reset", "password-change"].includes(
        form.id,
      )
    )
      return;
    event.preventDefault();
    if (form.id === "password-request-reset") {
      const email = String(new FormData(form).get("email") || "").trim();
      void perform(() => sendReset(email, form));
    } else void submitPassword(form);
  });
  root.addEventListener("click", (event) => {
    const action = event.target.closest?.("[data-password-action]")?.dataset
      .passwordAction;
    if (!action || ui.authPasswordBusy) return;
    if (action === "forgot") {
      const email = document
        .querySelector('input[name="email"]')
        ?.value?.trim();
      if (email) ui.authDraft = { ...ui.authDraft, email };
      resetToken = null;
      showScreen("forgot");
      document.querySelector('[name="email"]')?.focus();
    }
    if (action === "sign-in") backToSignIn();
    if (action === "resend-reset" && Date.now() >= resendAt)
      void perform(() => sendReset(ui.authDraft?.email || "", event.target.closest(".password-actions")));
    if (action === "cancel-change") location.reload();
    if (action === "finish-change") void perform(finishPasswordChange);
  });
}
